import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import { visit } from 'unist-util-visit'
import 'katex/dist/katex.min.css'
import { cn } from '@/lib/utils'

interface MathRendererProps {
    content: string;
    className?: string;
    inline?: boolean;
    breaks?: boolean;
}

/**
 * Helper to split a text string by a regex into new hast nodes.
 */
function splitTextByRegex(
    text: string,
    regex: RegExp,
    makeNode: (match: RegExpExecArray) => any
): any[] {
    const newNodes: any[] = [];
    let lastIndex = 0;
    let match;
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            newNodes.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        newNodes.push(makeNode(match));
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        newNodes.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return newNodes;
}

/**
 * A custom rehype plugin to find math expressions within HTML text nodes.
 * When `rehype-raw` parses HTML, standard Markdown math parsing is bypassed.
 * This plugin runs after `rehype-raw` and explicitly looks for $...$ and $$...$$
 * inside text nodes and converts them into math elements for `rehype-katex`.
 */
function rehypeParseMath() {
    return (tree: any) => {
        visit(tree, 'text', (node, index, parent) => {
            if (!parent || typeof index !== 'number') return;
            const text = node.value;
            // Match $$...$$ (display) or $...$ (inline, no newlines, no nested $)
            const mathRegex = /\$\$([\s\S]*?)\$\$|\$([^$\n]+?)\$/g;
            if (!mathRegex.test(text)) return;
            const newNodes = splitTextByRegex(text, mathRegex, (match) => {
                const isDisplay = match[0].startsWith('$$');
                const mathContent = match[1] ?? match[2];
                return {
                    type: 'element',
                    tagName: isDisplay ? 'div' : 'span',
                    properties: { className: ['math', isDisplay ? 'math-display' : 'math-inline'] },
                    children: [{ type: 'text', value: mathContent }]
                };
            });
            parent.children.splice(index, 1, ...newNodes);
            return index + newNodes.length;
        });
    };
}

/**
 * A custom rehype plugin to find markdown image syntax within HTML text nodes.
 * When `rehype-raw` parses HTML tags from the rich text editor, markdown image
 * syntax like ![alt](url) inside those tags is treated as raw text.
 * This plugin converts them into proper <img> elements.
 */
function rehypeParseImages() {
    return (tree: any) => {
        visit(tree, 'text', (node, index, parent) => {
            if (!parent || typeof index !== 'number') return;
            const text = node.value;
            // Match markdown image syntax: ![alt text](url)
            const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
            if (!imageRegex.test(text)) return;
            const newNodes = splitTextByRegex(text, imageRegex, (match) => ({
                type: 'element',
                tagName: 'img',
                properties: {
                    src: match[2],
                    alt: match[1],
                    style: 'max-width:100%;max-height:400px;border-radius:6px;border:1px solid #e4e4e7;display:block;margin:8px 0;object-fit:contain;'
                },
                children: []
            }));
            parent.children.splice(index, 1, ...newNodes);
            return index + newNodes.length;
        });
    };
}

/**
 * Normalize LaTeX dollar-sign escape sequences that AI models commonly emit.
 * \textdollar means a literal "$" in LaTeX text mode. We wrap it in an HTML
 * span so the dollar sign is inside an element node — remark-math skips
 * content inside inline HTML tags, and rehypeParseMath only visits text nodes,
 * so neither will mis-treat it as a math delimiter.
 */
function normalizeDollarEscapes(text: string): string {
    // Replace \textdollar (consuming any trailing space) with a span-wrapped $
    // so it is never treated as a KaTeX/remark-math inline math delimiter.
    return text.replace(/\\textdollar\s*/g, '<span>$</span>')
}

export function MathRenderer({ content, className, inline = false, breaks = true }: MathRendererProps) {
    if (!content) return null;

    const normalizedContent = normalizeDollarEscapes(content);

    return (
        <div className={cn(
            "prose prose-zinc max-w-none dark:prose-invert [&>p]:mb-2 [&>p:last-child]:mb-0 break-words",
            "[&_img]:bg-white",
            "[&_table]:border-collapse [&_table]:border [&_table]:border-gray-400 dark:[&_table]:border-zinc-600 [&_table]:w-full",
            "[&_th]:border [&_th]:border-gray-400 dark:[&_th]:border-zinc-600 [&_th]:bg-gray-100 dark:[&_th]:bg-zinc-700 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-gray-800 dark:[&_th]:text-zinc-100",
            "[&_td]:border [&_td]:border-gray-400 dark:[&_td]:border-zinc-600 [&_td]:px-3 [&_td]:py-2 [&_td]:text-gray-700 dark:[&_td]:text-zinc-300",
            inline && "inline [&>div]:inline [&>p]:inline [&_img]:inline-block [&_img]:align-middle [&_img]:max-h-8 [&_img]:mx-1",
            className
        )}>
            <ReactMarkdown
                remarkPlugins={breaks ? [remarkMath, remarkGfm, remarkBreaks] : [remarkMath, remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeParseImages, rehypeParseMath, rehypeKatex]}
                remarkRehypeOptions={{ allowDangerousHtml: true }}
                urlTransform={(url) => url}
                components={{
                    // Override p to avoid nesting issues if rendering inside another p or strict layout
                    p: ({ children }) => {
                        if (inline) return <span className="inline">{children}</span>
                        return <p className="mb-2 last:mb-0">{children}</p>
                    },
                    img: ({ src, alt, ...props }) => (
                        // Force white background on all images regardless of theme
                        <img
                            src={src}
                            alt={alt ?? ''}
                            {...props}
                            style={{ backgroundColor: '#ffffff', ...((props as any).style ?? {}) }}
                        />
                    )
                }}
            >
                {normalizedContent}
            </ReactMarkdown>
        </div>
    )
}
